import classNames from 'classnames';
import { Button } from 'devextreme-react/button';
import Toolbar, { Item as ToolbarItem } from 'devextreme-react/toolbar';
import { useScreenSize } from '../../../utils/media-query';
import { useAppSelector, useAppDispatch, selectMaxCountRegs } from '../../../contexts/hooks';
import { setPanelPined, setPanelVisible, setFilteredOrders, setFilteresToOrders, setFiltersToOrdersText } from '../../../contexts/slices/orders';
import { FilteredOrders } from '../../../types/types.orders';
import { useCallback, useEffect, useRef, useState } from 'react';
import DataGrid, { Column, Selection, Scrolling, Paging, FilterRow, HeaderFilter } from 'devextreme-react/data-grid';
import { Tooltip } from 'devextreme-react/tooltip';
import { useApiOrders } from '../../../api/api.orders';
import moment from 'moment';
import { LoadPanel } from 'devextreme-react/load-panel';
import { FilterPopup, newToast } from '../..';
import { filter } from '../../../images/icons';
import { ConvertLocalDate } from '../../../utils/functions';

export const ContactPanelDetails = () => {
  const dispatch = useAppDispatch();
  const { PanelVisible, PanelPined, CurrentOrder } = useAppSelector((x) => x.globalOrders);
  const [Loading, setLoading] = useState(false);
  const { FilteredOrders, FiltersToOrders, FiltersToOrdersText } = useAppSelector((x) => x.globalOrders);
  const MaxCountRegs = useAppSelector(selectMaxCountRegs);
  const contenedorRef = useRef<HTMLDivElement>(null);
  const [localPanelVisible, setLocalPanelVisible] = useState(PanelVisible);
  const { findOrder, GetOrdersByFilter } = useApiOrders();
  const gridRef = useRef<DataGrid>(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  // Key enfocada del DataGrid. Es estado local (no se toma directamente de Redux)
  // para que los cambios por interacción se reflejen síncronamente y evitar que
  // DevExtreme revierta el foco cuando el prop controlado queda stale respecto al
  // evento onFocusedRowChanged — esa reversión dispara el evento dos veces.
  const [focusedKey, setFocusedKey] = useState<number | undefined>(
    CurrentOrder?.IdOrder && CurrentOrder.IdOrder !== -1 ? CurrentOrder.IdOrder : undefined
  );
  const currentOrderIdRef = useRef<number | null>(CurrentOrder?.IdOrder ?? null);
  // Flag para deduplicar entre onFocusedRowChanged y onRowClick: cuando el foco
  // cambia por clic, DevExtreme dispara ambos eventos y los dos llamarían a findOrder.
  const focusChangeHandledRef = useRef(false);

  // Sincroniza el foco cuando CurrentOrder cambia desde fuera (Header, preorden, etc.)
  useEffect(() => {
    const id = CurrentOrder?.IdOrder;
    if (id && id !== -1) {
      currentOrderIdRef.current = id;
      setFocusedKey(id);
    }
  }, [CurrentOrder?.IdOrder]);

  // Auto-selecciona la primera orden cuando se carga un listado y aún no hay
  // orden actual (caso de entrar desde Dashboard o al refrescar con listado vacío).
  useEffect(() => {
    if (FilteredOrders.length > 0 && (!CurrentOrder?.IdOrder || CurrentOrder.IdOrder === -1)) {
      const firstId = FilteredOrders[0].Ord_Id;
      if (firstId && firstId !== currentOrderIdRef.current) {
        currentOrderIdRef.current = firstId;
        setFocusedKey(firstId);
        findOrder(undefined, firstId, setLoading);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [FilteredOrders]);
  const handlePined = () => {
    dispatch(setPanelPined(!PanelPined));
  };

  const { isLarge, isMedium } = useScreenSize();

  const hideModalFilter = useCallback(() => {
    setShowFilterModal(false);
  }, [setShowFilterModal]);

  const handleOpenModalFilter = useCallback(() => {
    setShowFilterModal(true);
  }, [setShowFilterModal]);

  useEffect(() => {
    setLocalPanelVisible(PanelVisible);
  }, [PanelVisible]);

  useEffect(() => {
    if (isLarge || isMedium) {
      setLocalPanelVisible(PanelVisible);
      return;
    }
    setLocalPanelVisible(false);
    dispatch(setPanelVisible(false));
  }, []);

  const handleClickOutside = (event: MouseEvent) => {
    if (localPanelVisible && !PanelPined) {
      if (contenedorRef.current && !contenedorRef.current.contains(event.target as Node)) {
        handlePanelClose();
      }
    }
  };

  useEffect(() => {
    if (localPanelVisible && !PanelPined) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
    // Limpia el evento cuando el componente se desmonta
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [localPanelVisible, PanelPined]);

  const handlePanelClose = () => {
    if (!PanelPined) {
      document.removeEventListener('click', handleClickOutside);
      dispatch(setPanelVisible(false));
    }
  };

  const handlePanelCloseInSide = () => {
    document.removeEventListener('click', handleClickOutside);
    dispatch(setPanelVisible(false));
  };

  // function handleRowClick(e: EventInfo<dxDataGrid<Order, any>>): void {
  //   const current = e.component.getSelectedRowsData();
  //   if (current.length === 0) return;
  //   findOrder(undefined, current[0].Ord_Id, setLoading);
  // }

  // onFocusedRowChanged: dispara en navegación por teclado y al hacer clic en otra fila.
  // Actualiza foco local de inmediato (para que DevExtreme no intente revertir el foco
  // al valor stale del prop controlado, que provoca un segundo disparo) y activa un
  // flag que bloquea al onRowClick subsecuente para evitar doble consumo.
  const handleFocusedRowChanged = (e) => {
    const rowId = e.row?.data?.Ord_Id;
    if (rowId && rowId !== currentOrderIdRef.current) {
      currentOrderIdRef.current = rowId;
      setFocusedKey(rowId);
      focusChangeHandledRef.current = true;
      findOrder(undefined, rowId, setLoading);
      // Liberar el flag tras el tick actual (onRowClick llega dentro del mismo ciclo
      // de eventos; si es navegación por teclado, el flag queda limpio para el próximo clic).
      setTimeout(() => {
        focusChangeHandledRef.current = false;
      }, 0);
    }
  };

  // onRowClick: único camino para recargar la fila ya enfocada (una sola fila en el
  // listado, o re-clic sobre la misma). Si onFocusedRowChanged acaba de atender este
  // clic, el flag lo aborta para evitar doble llamada.
  const handleRowClick = (e) => {
    if (focusChangeHandledRef.current) {
      focusChangeHandledRef.current = false;
      return;
    }
    const rowId = e.data?.Ord_Id;
    if (rowId) {
      currentOrderIdRef.current = rowId;
      setFocusedKey(rowId);
      findOrder(undefined, rowId, setLoading);
    }
  };

  useEffect(() => {
    // NO ejecutar handleRefresh si:
    // 1. Ya hay una orden cargada (búsqueda directa desde Header u otro módulo)
    // 2. Viene de redirect automático (useFilterAutoExecution lo manejará)
    // 3. Ya hay órdenes filtradas
    const hasCurrentOrder = CurrentOrder?.IdOrder && CurrentOrder.IdOrder !== -1;

    if (hasCurrentOrder) {
      // Búsqueda directa - no hacer nada, la orden ya está cargada
      return;
    }

    if (FilteredOrders.length === 0 && FiltersToOrders && !FiltersToOrders.Redirect) {
      handleRefresh();
    }
    // Asegurarnos de que el DataGrid reciba el foco cuando se monte el componente
    if (gridRef.current) {
      gridRef.current.instance.focus();
    }
  }, []); // Solo ejecutar al montar, no en cada cambio de FiltersToOrders

  const rowPrepared = (e) => {
    if (e.rowType === 'data') {
      const color = e.data.Se_Color;
      if (color && color.trim() !== '') {
        e.rowElement.style.color = color;
      }
      e.rowElement.setAttribute('id', `row-${e.data.Ord_Id}`);
    }
  };

  const handleRefresh = () => {
    if (FiltersToOrders) {
      setLoading(true);
      GetOrdersByFilter(FiltersToOrders)
        .then((res) => {
          if (res.length === 0) {
            newToast('INFO', 'No se encontraron resultados para el filtro aplicado', 5);
          }
          dispatch(setFilteredOrders(res));
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }
    newToast('WARNING', 'No hay filtros aplicados actualmente', 5);
  };

  // Limpiar filtros y aplicar solo fecha de hoy
  const handleClearFilters = useCallback(() => {
    const today = ConvertLocalDate(new Date());
    const defaultFilters: FilteredOrders = {
      Fechas: [today, today],
      Areas: [],
      Estudios: [],
      Servicios: [],
      Estados: [],
      Sedes: [],
      Tracking: undefined,
      Remitido: undefined,
      Redirect: false,
    };

    setLoading(true);
    GetOrdersByFilter(defaultFilters)
      .then((res) => {
        dispatch(setFilteredOrders(res));
        dispatch(setFilteresToOrders(defaultFilters));
        dispatch(setFiltersToOrdersText([]));
        if (res.length === 0) {
          newToast('INFO', 'No se encontraron órdenes para el día de hoy', 5);
        }
      })
      .catch((error) => {
        console.error('Error al limpiar filtros:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [GetOrdersByFilter, dispatch]);

  return (
    <div id='contact-panel' ref={contenedorRef} className={classNames({ panel: true, open: localPanelVisible, pin: PanelPined && (isLarge || isMedium), unpin: !PanelPined })}>
      <div className='data-wrapper'>
        <Toolbar className='panel-toolbar'>
          <ToolbarItem location='before'>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {FilteredOrders.length === MaxCountRegs && (
                <span id='maxRegsWarning' style={{ color: '#f0ad4e', fontWeight: 'bold', fontSize: '16px', lineHeight: 1, userSelect: 'none', width: '10px', textAlign: 'center' }}>
                  !
                </span>
              )}
              <span className='contact-name value' style={FilteredOrders.length === MaxCountRegs ? { color: '#f0ad4e' } : undefined}>
                {FilteredOrders.length} Órdenes
              </span>
            </div>
            {FilteredOrders.length === MaxCountRegs && (
              <Tooltip target='#maxRegsWarning' showEvent='mouseenter' hideEvent='mouseleave' position='bottom'>
                <span>Se ha limitado a {MaxCountRegs} registros para evitar lentitud</span>
              </Tooltip>
            )}
          </ToolbarItem>
          <ToolbarItem location='after' visible={isLarge || isMedium}>
            <Button icon={PanelPined ? 'pin' : 'unpin'} onClick={handlePined} />
          </ToolbarItem>
          {/* Botón para limpiar filtros - solo visible cuando hay filtros aplicados */}
          <ToolbarItem location='after' visible={(isLarge || isMedium) && FiltersToOrdersText && FiltersToOrdersText.length > 0}>
            <Button id='clearFiltersButton' icon='clearformat' onClick={handleClearFilters} />
            <Tooltip target='#clearFiltersButton' showEvent='mouseenter' hideEvent='mouseleave' position='bottom'>
              <span>Limpiar filtros</span>
            </Tooltip>
          </ToolbarItem>
          <ToolbarItem location={'after'}>
            <div className='filter-button-container'>
              <Button id='filterButtonHeader' icon={filter} onClick={handleOpenModalFilter} hint={FiltersToOrdersText && FiltersToOrdersText.length > 0 ? '' : 'Filtrar Ordenes'} />
              {/* Badge de notificación para filtros aplicados - solo cuando hay filtros */}
              {(isLarge || isMedium) && FiltersToOrdersText && FiltersToOrdersText.length > 0 && <div className='filter-badge'>{FiltersToOrdersText.length}</div>}
              {/* Tooltip para mostrar filtros aplicados - solo cuando hay filtros */}
              {(isLarge || isMedium) && FiltersToOrdersText && FiltersToOrdersText.length > 0 && (
                <Tooltip
                  target='#filterButtonHeader'
                  showEvent='mouseenter'
                  hideEvent='mouseleave'
                  position='bottom'
                  contentComponent={() => (
                    <div className='filters-tooltip-content'>
                      <div className='filters-tooltip-title'>
                        <strong>Filtros aplicados:</strong>
                      </div>
                      {FiltersToOrdersText.map((filter, index) => (
                        <div key={index} className='filters-tooltip-item'>
                          {filter}
                        </div>
                      ))}
                    </div>
                  )}
                />
              )}
            </div>
          </ToolbarItem>
          <ToolbarItem location='after'>
            <Button icon='refresh' onClick={handleRefresh} />
          </ToolbarItem>
          <ToolbarItem location='after'>
            <Button icon='close' onClick={handlePanelCloseInSide} />
          </ToolbarItem>
        </Toolbar>
        <DataGrid dataSource={FilteredOrders} tabIndex={0} height='calc(100% - 50px)' onFocusedRowChanged={handleFocusedRowChanged} onRowClick={handleRowClick} ref={gridRef} focusedRowEnabled={true} focusedRowKey={focusedKey} autoNavigateToFocusedRow={true} keyExpr='Ord_Id' id='currentOrders' onRowPrepared={rowPrepared} className='gridOrders compact-grid' noDataText='No hay órdenes para mostrar' hoverStateEnabled={true} repaintChangesOnly={true}>
          <Paging enabled={false} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <Selection mode='single' />
          <Scrolling mode='virtual' rowRenderingMode='virtual' />
          <Column dataField='Ord_Numero' caption='ORDEN' width={100}>
            <HeaderFilter />
          </Column>
          <Column dataField='Pc_Nombre' caption='USUARIO' calculateCellValue={(rowData) => `${rowData.Pc_Nombre || ''} ${rowData.Pc_Apellido1 || ''} ${rowData.Pc_Apellido2 || ''}`} calculateDisplayValue={(rowData) => `${rowData.Pc_Nombre || ''} ${rowData.Pc_Apellido1 || ''} ${rowData.Pc_Apellido2 || ''}`}>
            <HeaderFilter />
          </Column>
          <Column dataField='Ord_FechaCreacion' caption='FECHA' width={100} dataType='date' format='dd/MM/yyyy' visible={false}>
            <HeaderFilter />
          </Column>
        </DataGrid>
        <LoadPanel position={{ of: '#currentOrders' }} visible={Loading} showIndicator={true} />
      </div>
      {FilteredOrders.map((order) => {
        return (
          <Tooltip key={order.Ord_Id} target={`#row-${order.Ord_Id}`} showEvent='mouseenter' hideEvent='mouseleave' position='left'>
            <div className='toolTipFilter'>
              <div>
                <strong>Fecha de creación:</strong> {moment(order.Column1).format('DD/MM/YYYY')}
              </div>
              <div>
                <strong>Orden:</strong> {order.Ord_Numero}
              </div>
              <div>
                <strong>Usuario:</strong> {order.Pc_Nombre} {order.Pc_Apellido1 || ''} {order.Pc_Apellido2 || ''}
              </div>
            </div>
          </Tooltip>
        );
      })}
      <FilterPopup visible={showFilterModal} setVisible={hideModalFilter} /> 
    </div>
  );
};
